import React from "react";
import { Link } from "../../design/link";
import { ChevronLeftIcon } from "@heroicons/react/20/solid";
import { Heading, Subheading } from "../../design/heading";
import ChildDetailFileList from "./components/ChildDetailFilesList";
import {
  DescriptionDetails,
  DescriptionList,
  DescriptionTerm,
} from "../../design/description-list";
import EditChildButton from "./components/EditChildButton";
import LoadingView from "../../components/LoadingView";
import { Avatar } from "../../design/avatar";
import { getInitials } from "../../utils/string";
import ErrorView from "../../components/ErrorView";
import { Badge } from "../../design/badge";
import { Divider } from "../../design/divider";
import {
  ChildHeaderProps,
  ChildDetailsProps,
} from "./types";
import { useChildDetail } from "./hooks/useChildDetail";
import {
  RACE_LABELS,
  SEX_LABELS,
  LANGUAGE_LABELS,
  SPECIAL_NEEDS_LABELS,
  VICTIMIZATION_TYPE_LABELS,
  Race,
  Sex,
  PrimaryLanguage,
  SpecialNeed,
  VictimizationType,
} from "@visitproof/types";
import { Child, getAttorneyDisplayName } from "@visitproof/types";
import moment from "moment";
import { useAttorney } from "../../contexts/AttorneyContext";

// Breadcrumb Navigation
const Breadcrumb: React.FC = () => (
  <div className="max-lg:hidden">
    <Link
      href="/children"
      className="inline-flex items-center gap-2 transition-colors text-sm/6 text-zinc-500 dark:text-zinc-400 hover:text-zinc-700 dark:hover:text-zinc-300"
    >
      <ChevronLeftIcon className="size-4 fill-zinc-400 dark:fill-zinc-500" />
      Children
    </Link>
  </div>
);

// Child Header with Avatar
const ChildHeader: React.FC<ChildHeaderProps> = React.memo(({ child, ageInfo }) => (
  <div className="mt-4 lg:mt-8">
    <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
      <div className="flex items-center gap-4">
        <Avatar
          src={child.avatarUrl}
          alt={`${child.firstName} ${child.lastName}`}
          className="rounded-full w-14 h-14"
          initials={getInitials(`${child.firstName} ${child.lastName}`)}
        />
        <div>
          <Heading>
            {child.firstName} {child.lastName}
          </Heading>
          {ageInfo.age !== null && (
            <p className="mt-1 text-sm text-zinc-500 dark:text-zinc-400">
              {ageInfo.age} years old
            </p>
          )}
        </div>
      </div>
      <EditChildButton child={child} />
    </div>
  </div>
));

// Basic Information Card
const BasicInfoCard: React.FC<ChildDetailsProps> = React.memo(({ child, caseInfo, ageInfo }) => (
  <div className="p-6 bg-white border shadow-sm rounded-xl dark:bg-zinc-900 border-zinc-200 dark:border-zinc-700">
    <Subheading>Basic Information</Subheading>
    <Divider className="my-4" />
    <DescriptionList>
      <DescriptionTerm>Date of Birth</DescriptionTerm>
      <DescriptionDetails>
        {ageInfo.formattedDate}
      </DescriptionDetails>

      <DescriptionTerm>Case</DescriptionTerm>
      <DescriptionDetails>
        <Link
          href={`/case/${child.caseId}`}
          className="text-blue-600 dark:text-blue-400 hover:underline"
        >
          {caseInfo?.identifier ?? child.caseId}
        </Link>
      </DescriptionDetails>

      {child.allergies && (
        <>
          <DescriptionTerm>Allergies</DescriptionTerm>
          <DescriptionDetails>{child.allergies}</DescriptionDetails>
        </>
      )}

      {child.notes && (
        <>
          <DescriptionTerm>Notes</DescriptionTerm>
          <DescriptionDetails className="whitespace-pre-wrap">
            {child.notes}
          </DescriptionDetails>
        </>
      )}
    </DescriptionList>
  </div>
));

// Demographics Card
const DemographicsCard: React.FC<{ child: Child }> = React.memo(({ child }) => {
  const hasDemographics = child.race || child.sex || child.primaryLanguage ||
    (child.specialNeeds && child.specialNeeds.length > 0) || child.hasSpecialNeeds;

  return (
    <div className="p-6 bg-white border shadow-sm rounded-xl dark:bg-zinc-900 border-zinc-200 dark:border-zinc-700">
      <Subheading>Demographics</Subheading>
      <Divider className="my-4" />
      {!hasDemographics ? (
        <p className="text-sm text-zinc-500 dark:text-zinc-400">
          No demographics information recorded. Edit to add demographics for grant reporting.
        </p>
      ) : (
        <DescriptionList>
          {child.sex && (
            <>
              <DescriptionTerm>Sex</DescriptionTerm>
              <DescriptionDetails>
                {SEX_LABELS[child.sex as Sex] || child.sex}
              </DescriptionDetails>
            </>
          )}

          {child.race && (
            <>
              <DescriptionTerm>Race/Ethnicity</DescriptionTerm>
              <DescriptionDetails>
                {RACE_LABELS[child.race as Race] || child.race}
              </DescriptionDetails>
            </>
          )}

          {child.primaryLanguage && (
            <>
              <DescriptionTerm>Primary Language</DescriptionTerm>
              <DescriptionDetails>
                {child.primaryLanguage === "other" && child.primaryLanguageOther
                  ? child.primaryLanguageOther
                  : LANGUAGE_LABELS[child.primaryLanguage as PrimaryLanguage] || child.primaryLanguage}
              </DescriptionDetails>
            </>
          )}

          <DescriptionTerm>Special Needs</DescriptionTerm>
          <DescriptionDetails>
            {child.hasSpecialNeeds ? "Yes" : "No"}
          </DescriptionDetails>

          {child.specialNeeds && child.specialNeeds.length > 0 && (
            <>
              <DescriptionTerm>Special Needs Categories</DescriptionTerm>
              <DescriptionDetails>
                <div className="flex flex-wrap gap-2">
                  {child.specialNeeds.map((need) => (
                    <Badge key={need} color="blue">
                      {SPECIAL_NEEDS_LABELS[need as SpecialNeed] || need}
                    </Badge>
                  ))}
                </div>
              </DescriptionDetails>
            </>
          )}

          {child.specialNeedsNotes && (
            <>
              <DescriptionTerm>Special Needs Notes</DescriptionTerm>
              <DescriptionDetails className="whitespace-pre-wrap">
                {child.specialNeedsNotes}
              </DescriptionDetails>
            </>
          )}
        </DescriptionList>
      )}
    </div>
  );
});

// Custody & Guardian Card
const CustodyGuardianCard: React.FC<{ child: Child }> = React.memo(({ child }) => {
  const hasCustodyInfo = child.residesWithName || child.legalGuardianName || child.lastContactWithVisitingParent;

  return (
    <div className="p-6 bg-white border shadow-sm rounded-xl dark:bg-zinc-900 border-zinc-200 dark:border-zinc-700">
      <Subheading>Custody & Guardian</Subheading>
      <Divider className="my-4" />
      {!hasCustodyInfo ? (
        <p className="text-sm text-zinc-500 dark:text-zinc-400">
          No custody or guardian information recorded.
        </p>
      ) : (
        <DescriptionList>
          {child.residesWithName && (
            <>
              <DescriptionTerm>Resides With</DescriptionTerm>
              <DescriptionDetails>{child.residesWithName}</DescriptionDetails>
            </>
          )}

          {child.legalGuardianName && (
            <>
              <DescriptionTerm>Legal Guardian</DescriptionTerm>
              <DescriptionDetails>{child.legalGuardianName}</DescriptionDetails>
            </>
          )}

          {child.lastContactWithVisitingParent && (
            <>
              <DescriptionTerm>Last Contact with Visiting Parent</DescriptionTerm>
              <DescriptionDetails>
                {moment(child.lastContactWithVisitingParent.toDate()).format("MMMM Do, YYYY")}
              </DescriptionDetails>
            </>
          )}
        </DescriptionList>
      )}
    </div>
  );
});

// DSS / Social Services Card
const DSSCard: React.FC<{ child: Child }> = React.memo(({ child }) => {
  const hasDSSInfo = child.isInDSSCustody !== undefined || child.socialWorkerName || child.socialWorkerPhone;

  return (
    <div className="p-6 bg-white border shadow-sm rounded-xl dark:bg-zinc-900 border-zinc-200 dark:border-zinc-700">
      <Subheading>DSS / Social Services</Subheading>
      <Divider className="my-4" />
      {!hasDSSInfo ? (
        <p className="text-sm text-zinc-500 dark:text-zinc-400">
          No DSS or social services information recorded.
        </p>
      ) : (
        <DescriptionList>
          {child.isInDSSCustody !== undefined && (
            <>
              <DescriptionTerm>In DSS Custody</DescriptionTerm>
              <DescriptionDetails>
                <Badge color={child.isInDSSCustody ? "amber" : "zinc"}>
                  {child.isInDSSCustody ? "Yes" : "No"}
                </Badge>
              </DescriptionDetails>
            </>
          )}

          {child.socialWorkerName && (
            <>
              <DescriptionTerm>Social Worker Name</DescriptionTerm>
              <DescriptionDetails>{child.socialWorkerName}</DescriptionDetails>
            </>
          )}

          {child.socialWorkerPhone && (
            <>
              <DescriptionTerm>Social Worker Phone</DescriptionTerm>
              <DescriptionDetails>{child.socialWorkerPhone}</DescriptionDetails>
            </>
          )}
        </DescriptionList>
      )}
    </div>
  );
});

// Medical Information Card
const MedicalInfoCard: React.FC<{ child: Child }> = React.memo(({ child }) => {
  const hasMedicalInfo = child.medicalProblems || child.allergies;

  return (
    <div className="p-6 bg-white border shadow-sm rounded-xl dark:bg-zinc-900 border-zinc-200 dark:border-zinc-700">
      <Subheading>Medical Information</Subheading>
      <Divider className="my-4" />
      {!hasMedicalInfo ? (
        <p className="text-sm text-zinc-500 dark:text-zinc-400">
          No medical information recorded.
        </p>
      ) : (
        <DescriptionList>
          {child.medicalProblems && (
            <>
              <DescriptionTerm>Medical Problems</DescriptionTerm>
              <DescriptionDetails className="whitespace-pre-wrap">
                {child.medicalProblems}
              </DescriptionDetails>
            </>
          )}

          {child.allergies && (
            <>
              <DescriptionTerm>Allergies</DescriptionTerm>
              <DescriptionDetails>{child.allergies}</DescriptionDetails>
            </>
          )}
        </DescriptionList>
      )}
    </div>
  );
});

// Victimization Information Card
const VictimizationCard: React.FC<{ child: Child }> = React.memo(({ child }) => {
  const hasVictimizationInfo = child.victimizationType ||
    child.hasWitnessedAbuse !== undefined ||
    child.hasIntervenedInAbuse !== undefined;

  return (
    <div className="p-6 bg-white border shadow-sm rounded-xl dark:bg-zinc-900 border-zinc-200 dark:border-zinc-700">
      <Subheading>Victimization Information</Subheading>
      <Divider className="my-4" />
      {!hasVictimizationInfo ? (
        <p className="text-sm text-zinc-500 dark:text-zinc-400">
          No victimization information recorded.
        </p>
      ) : (
        <DescriptionList>
          {child.victimizationType && (
            <>
              <DescriptionTerm>Victimization Type</DescriptionTerm>
              <DescriptionDetails>
                {child.victimizationType === "other" && child.victimizationOther
                  ? child.victimizationOther
                  : VICTIMIZATION_TYPE_LABELS[child.victimizationType as VictimizationType] || child.victimizationType}
              </DescriptionDetails>
            </>
          )}

          {child.hasWitnessedAbuse !== undefined && (
            <>
              <DescriptionTerm>Has Witnessed Abuse</DescriptionTerm>
              <DescriptionDetails>
                <Badge color={child.hasWitnessedAbuse ? "red" : "zinc"}>
                  {child.hasWitnessedAbuse ? "Yes" : "No"}
                </Badge>
              </DescriptionDetails>
            </>
          )}

          {child.hasIntervenedInAbuse !== undefined && (
            <>
              <DescriptionTerm>Has Intervened in Abuse</DescriptionTerm>
              <DescriptionDetails>
                <Badge color={child.hasIntervenedInAbuse ? "amber" : "zinc"}>
                  {child.hasIntervenedInAbuse ? "Yes" : "No"}
                </Badge>
              </DescriptionDetails>
            </>
          )}

          {child.interventionDetails && (
            <>
              <DescriptionTerm>Intervention Details</DescriptionTerm>
              <DescriptionDetails className="whitespace-pre-wrap">
                {child.interventionDetails}
              </DescriptionDetails>
            </>
          )}
        </DescriptionList>
      )}
    </div>
  );
});

// Attorneys/Advocates Card
const AttorneysCard: React.FC<{ child: Child }> = React.memo(({ child }) => {
  const { attorneys } = useAttorney();

  // Get attorneys/advocates associated with this child
  const childAttorneys = (child.attorneyIds || [])
    .map((id) => attorneys.find((a) => a.id === id))
    .filter(Boolean);

  return (
    <div className="p-6 bg-white border shadow-sm rounded-xl dark:bg-zinc-900 border-zinc-200 dark:border-zinc-700">
      <Subheading>Attorneys & Advocates</Subheading>
      <Divider className="my-4" />
      {childAttorneys.length === 0 ? (
        <p className="text-sm text-zinc-500 dark:text-zinc-400">
          No attorneys or advocates assigned. GAL, Minor's Counsel, or CASA can be assigned from the case.
        </p>
      ) : (
        <DescriptionList>
          <DescriptionTerm>Assigned</DescriptionTerm>
          <DescriptionDetails>
            <div className="flex flex-col gap-1">
              {childAttorneys.map((attorney) => (
                <Link
                  key={attorney!.id}
                  href={`/attorney/${attorney!.id}`}
                  className="text-blue-600 dark:text-blue-400 hover:underline"
                >
                  {getAttorneyDisplayName(attorney!)}
                  {attorney!.firmName && (
                    <span className="text-zinc-500"> — {attorney!.firmName}</span>
                  )}
                </Link>
              ))}
            </div>
          </DescriptionDetails>
        </DescriptionList>
      )}
    </div>
  );
});

const ChildDetailPage: React.FC = () => {
  const { selectedChild, loading, error, caseInfo, ageInfo } = useChildDetail();

  // Early returns for loading and error states
  if (loading.details) {
    return <LoadingView />;
  }

  if (error.details) {
    return <ErrorView error={error.details} />;
  }

  if (!selectedChild || !selectedChild.id) {
    return <ErrorView error={new Error("Child not found")} />;
  }

  const childId = selectedChild.id;

  return (
    <div className="space-y-6">
      {/* Breadcrumb Navigation */}
      <Breadcrumb />

      {/* Child Header with Age */}
      <ChildHeader child={selectedChild} ageInfo={ageInfo} />

      {/* Basic Info & Demographics - Two Column Layout on Desktop */}
      <div className="grid gap-6 lg:grid-cols-2">
        <BasicInfoCard
          child={selectedChild}
          caseInfo={caseInfo}
          ageInfo={ageInfo}
        />
        <DemographicsCard child={selectedChild} />
      </div>

      {/* Custody & Medical Info */}
      <div className="grid gap-6 lg:grid-cols-2">
        <CustodyGuardianCard child={selectedChild} />
        <MedicalInfoCard child={selectedChild} />
      </div>

      {/* DSS & Victimization Info */}
      <div className="grid gap-6 lg:grid-cols-2">
        <DSSCard child={selectedChild} />
        <VictimizationCard child={selectedChild} />
      </div>

      {/* Attorneys & Files */}
      <div className="grid gap-6 lg:grid-cols-2">
        <AttorneysCard child={selectedChild} />
        <div className="p-6 bg-white border shadow-sm rounded-xl dark:bg-zinc-900 border-zinc-200 dark:border-zinc-700">
          <ChildDetailFileList childId={childId} />
        </div>
      </div>
    </div>
  );
};

export default ChildDetailPage;
